import React, { useState, useEffect } from 'react';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import { auth, db } from '../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../auth/cache_manager';
import { 
  FaLightbulb,
  FaBook,
  FaCube,
  FaClone,
  FaMicrosoft,
  FaStickyNote
} from 'react-icons/fa';
import NotebookModal from './take_notes/notebooks';
import './take_notes.css';

function TakeNotes() {
  // # State for modal and dark mode
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNotebook, setSelectedNotebook] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Handle notebook click
  const handleNotebookClick = (notebookId) => {
    setSelectedNotebook(notebookId);
    setModalOpen(true);
  };

  // # Define sections with icons and descriptions
  const sections = {
    goodNotes: {
      title: "Taking a good note",
      items: [
        {
          title: "General Tips",
          icon: <FaLightbulb className="ext_tknts_section-icon" />,
          description: "Essential tips for effective note-taking"
        }
      ]
    },
    platforms: {
      title: "Where to take notes?",
      items: [
        {
          title: "Notion",
          icon: <FaBook className="ext_tknts_section-icon" />,
          description: "All-in-one workspace for notes and collaboration",
          id: 'notion'
        },
        {
          title: "Obsidian Notebook",
          icon: <FaCube className="ext_tknts_section-icon" />,
          description: "Knowledge base with markdown support",
          id: 'obsidian'
        },
        {
          title: "Anki Flashcards",
          icon: <FaClone className="ext_tknts_section-icon" />,
          description: "Spaced repetition flashcard system",
          id: 'anki'
        },
        {
          title: "OneNote Notebook",
          icon: <FaMicrosoft className="ext_tknts_section-icon" />,
          description: "Microsoft's digital note-taking app",
          id: 'onenote'
        },
        {
          title: "UpNotes Notebook",
          icon: <FaStickyNote className="ext_tknts_section-icon" />,
          description: "Simple and clean note-taking app",
          id: 'upnotes'
        }
      ]
    }
  };

  return (
    <div className={`ext_tknts_page-container ${isDarkMode ? 'ext_tknts_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <main className="ext_tknts_main-content">
        <div className="ext_tknts_header">
          <h1>Taking Notes</h1>
          <p>Learn effective note-taking strategies and choose your preferred platform</p>
        </div>

        {/* Good Notes Section */}
        <div className="ext_tknts_section">
          <h2 className="ext_tknts_section-title">{sections.goodNotes.title}</h2>
          <div className="ext_tknts_tips-grid">
            {sections.goodNotes.items.map((item, index) => (
              <div key={index} className="ext_tknts_tip-card">
                <div className="ext_tknts_icon-container">
                  {item.icon}
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <div className="ext_tknts_coming-soon">Coming Soon</div>
              </div>
            ))}
          </div>
        </div>

        {/* Platforms Section */}
        <div className="ext_tknts_section">
          <h2 className="ext_tknts_section-title">{sections.platforms.title}</h2>
          <div className="ext_tknts_platforms-grid">
            {sections.platforms.items.map((item, index) => (
              <div 
                key={index} 
                className="ext_tknts_platform-card"
                onClick={() => handleNotebookClick(item.id)}
              >
                <div className="ext_tknts_icon-container">
                  {item.icon}
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Notebook Modal */}
      <NotebookModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        notebook={selectedNotebook}
        isDarkMode={isDarkMode}
      />

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default TakeNotes;
